import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { chatAPI, clientsAPI, projectsAPI, usersAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  MessageCircle, HelpCircle, CheckCircle2, AlertCircle, ClipboardList,
  Plus, Search, Send, MoreHorizontal, Trash2, MessagesSquare,
  Inbox, ChevronRight, CheckCheck, ArrowLeft, Mic, Square, X, Pencil, Loader2,
} from 'lucide-react';
import { playNotificationSound, unlockAudio } from '../lib/sound';

// ── Type config ──────────────────────────────────────────────
const MSG_TYPES = {
  info:      { label: 'Info',      icon: MessageCircle,  bg: 'bg-slate-100',  text: 'text-slate-600',  border: 'border-slate-200' },
  domanda:   { label: 'Domanda',   icon: HelpCircle,     bg: 'bg-amber-50',   text: 'text-amber-700',  border: 'border-amber-200' },
  decisione: { label: 'Decisione', icon: CheckCircle2,   bg: 'bg-emerald-50', text: 'text-emerald-700',border: 'border-emerald-200' },
  urgente:   { label: 'Urgente',   icon: AlertCircle,    bg: 'bg-red-50',     text: 'text-red-700',    border: 'border-red-200' },
  task:      { label: 'Task',      icon: ClipboardList,  bg: 'bg-violet-50',  text: 'text-violet-700', border: 'border-violet-200' },
};

const PRIORITY_CONFIG = {
  bassa:    { label: 'Bassa',    dot: 'bg-slate-400' },
  media:    { label: 'Media',    dot: 'bg-amber-400' },
  alta:     { label: 'Alta',     dot: 'bg-orange-500' },
  critica:  { label: 'Critica',  dot: 'bg-red-600' },
};

const STATUS_CONFIG = {
  aperta:     { label: 'Aperta',     class: 'bg-emerald-100 text-emerald-700' },
  in_attesa:  { label: 'In attesa',  class: 'bg-amber-100 text-amber-700' },
  risolta:    { label: 'Risolta',    class: 'bg-slate-100 text-slate-600' },
};

const STATUSES = ['aperta', 'in_attesa', 'risolta'];

// ── Helpers ───────────────────────────────────────────────────
const formatMsgTime = (iso) => {
  if (!iso) return '';
  try { return format(new Date(iso), 'HH:mm dd/MM', { locale: it }); }
  catch { return ''; }
};

const userInitials = (user) =>
  user ? `${user.nome?.[0] ?? ''}${user.cognome?.[0] ?? ''}`.toUpperCase() : '?';


// ── Main Component ────────────────────────────────────────────
export const Chat = () => {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [focus, setFocus] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  const [activeTab, setActiveTab] = useState('conversations');
  // Mobile: 'list' mostra il pannello sinistro, 'chat' mostra i messaggi
  const [mobileView, setMobileView] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [newMsgText, setNewMsgText] = useState('');
  const [newMsgType, setNewMsgType] = useState('info');
  const [sending, setSending] = useState(false);
  const [destinatari, setDestinatari] = useState([]);
  const [newConvOpen, setNewConvOpen] = useState(false);
  const [newConv, setNewConv] = useState({
    titolo: '', contesto: 'generale', client_id: '', project_id: '', priorita: '', assegnato_a: '',
  });

  // Edit message state
  const [editingMsg, setEditingMsg] = useState(null); // message object being edited
  const [editTesto, setEditTesto] = useState('');
  const [editTipo, setEditTipo] = useState('info');
  const [editDestinatari, setEditDestinatari] = useState([]);

  // Search state
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = no search yet
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  const [highlightMsgId, setHighlightMsgId] = useState(null);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const knownMessageIds = useRef(new Set());
  const isFirstLoad = useRef(true);
  const currentUserRef = useRef(currentUser);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);

  // ── Audio recording state ──────────────────────────────────
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [pendingAudio, setPendingAudio] = useState(null); // { data: base64, mime: string, durationSec }
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  useEffect(() => {
    unlockAudio();
  }, []);

  useEffect(() => {
    Promise.all([clientsAPI.getAll(), usersAPI.getAll()])
      .then(([c, u]) => { setClients(c.data); setUsers(u.data); });
    loadConversations();
    loadFocus();
  }, []);

  useEffect(() => {
    const id = setInterval(() => { loadConversations(); loadFocus(); }, 10000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!selectedConv) return;
    knownMessageIds.current = new Set();
    isFirstLoad.current = true;
    loadMessages(selectedConv.id);
    const id = setInterval(() => loadMessages(selectedConv.id), 5000);
    return () => clearInterval(id);
  }, [selectedConv?.id]);

  useEffect(() => {
    if (highlightMsgId) {
      const el = document.getElementById(`msg-${highlightMsgId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Clear highlight after animation
        const t = setTimeout(() => setHighlightMsgId(null), 3000);
        return () => clearTimeout(t);
      }
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, highlightMsgId]);

  const loadConversations = async () => {
    try {
      const res = await chatAPI.getConversations();
      setConversations(res.data);
      // Se c'è ?conv=ID nell'URL, apri quella conversazione
      const convId = searchParams.get('conv');
      if (convId) {
        const target = res.data.find(c => c.id === convId);
        if (target) {
          setSelectedConv(target);
          setMobileView('chat');
          setSearchParams({}, { replace: true });
        }
      }
    } catch {}
  };

  const loadMessages = async (convId) => {
    try {
      const res = await chatAPI.getMessages(convId);
      const incoming = res.data;
      if (isFirstLoad.current) {
        incoming.forEach(m => knownMessageIds.current.add(m.id));
        isFirstLoad.current = false;
      } else {
        const newFromOthers = incoming.filter(
          m => !knownMessageIds.current.has(m.id) && m.user_id !== currentUserRef.current?.id
        );
        if (newFromOthers.length > 0) {
          playNotificationSound();
          newFromOthers.forEach(m => knownMessageIds.current.add(m.id));
        }
        incoming.forEach(m => knownMessageIds.current.add(m.id));
      }
      setMessages(incoming);
    } catch {}
  };

  const loadFocus = async () => {
    try { const res = await chatAPI.getFocus(); setFocus(res.data); } catch {}
  };

  const handleSearchChange = (value) => {
    setSearchQ(value);
    clearTimeout(searchTimeoutRef.current);
    if (value.trim().length < 2) { setSearchResults(null); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await chatAPI.search(value.trim());
        setSearchResults(res.data);
      } catch { setSearchResults({ results: [], total: 0 }); }
      finally { setSearchLoading(false); }
    }, 350);
  };

  const handleSelectConv = (conv, msgId = null) => {
    setHighlightMsgId(msgId);
    setSelectedConv(conv);
    setActiveTab('conversations');
    loadMessages(conv.id);
    setMobileView('chat');
  };

  const handleBack = () => {
    setMobileView('list');
    setSelectedConv(null);
  };

  const startRecording = async () => {
    if (!selectedConv) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          setPendingAudio({ data: base64, mime: mimeType, durationSec: recordingSeconds });
        };
        reader.readAsDataURL(blob);
        clearInterval(recordingTimerRef.current);
        setIsRecording(false);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecordingSeconds(0);
      setIsRecording(true);
      recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
    } catch {
      toast.error('Microfono non disponibile o permesso negato');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const cancelAudio = () => {
    if (isRecording) {
      clearInterval(recordingTimerRef.current);
      mediaRecorderRef.current?.stream?.getTracks().forEach(t => t.stop());
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
    }
    setPendingAudio(null);
    audioChunksRef.current = [];
  };

  const sendAudio = async () => {
    if (!pendingAudio || !selectedConv) return;
    setSending(true);
    try {
      await chatAPI.sendMessage(selectedConv.id, {
        testo: '',
        tipo: 'info',
        destinatari,
        audio_data: pendingAudio.data,
        audio_mime: pendingAudio.mime,
      });
      setPendingAudio(null);
      setDestinatari([]);
      await loadMessages(selectedConv.id);
      await loadConversations(); await loadFocus();
    } catch { toast.error('Errore nell\'invio audio'); }
    finally { setSending(false); }
  };

  const handleSend = async () => {
    if (!newMsgText.trim() || !selectedConv) return;
    setSending(true);
    try {
      await chatAPI.sendMessage(selectedConv.id, { testo: newMsgText.trim(), tipo: newMsgType, destinatari });
      setDestinatari([]); setNewMsgText(''); setNewMsgType('info');
      await loadMessages(selectedConv.id);
      await loadConversations(); await loadFocus();
    } catch { toast.error('Errore nell\'invio'); }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const openEditMsg = (msg) => {
    setEditingMsg(msg);
    setEditTesto(msg.testo || '');
    setEditTipo(msg.tipo || 'info');
    setEditDestinatari(msg.destinatari || []);
  };

  const handleUpdateMsg = async () => {
    if (!editingMsg) return;
    try {
      await chatAPI.updateMessage(editingMsg.id, {
        testo: editTesto,
        tipo: editTipo,
        destinatari: editDestinatari,
      });
      setEditingMsg(null);
      await loadMessages(selectedConv.id);
      await loadFocus();
      toast.success('Messaggio aggiornato');
    } catch { toast.error('Errore aggiornamento messaggio'); }
  };

  const handleResolve = async (msgId) => {
    try {
      await chatAPI.resolveMessage(msgId);
      await loadMessages(selectedConv.id); await loadFocus();
      toast.success('Domanda segnata come risolta');
    } catch { toast.error('Errore'); }
  };

  const handleStatusChange = async (convId, stato) => {
    try {
      await chatAPI.updateStatus(convId, stato);
      await loadConversations();
      if (selectedConv?.id === convId) setSelectedConv(prev => ({ ...prev, stato }));
    } catch { toast.error('Errore aggiornamento stato'); }
  };

  const handleDelete = async (convId) => {
    if (!window.confirm('Eliminare questa conversazione e tutti i messaggi?')) return;
    try {
      await chatAPI.deleteConversation(convId);
      if (selectedConv?.id === convId) { setSelectedConv(null); setMessages([]); setMobileView('list'); }
      await loadConversations();
      toast.success('Conversazione eliminata');
    } catch { toast.error('Errore eliminazione'); }
  };

  const handleCreateConv = async () => {
    if (!newConv.titolo.trim()) return;
    try {
      const payload = {
        titolo: newConv.titolo.trim(), contesto: newConv.contesto,
        client_id: newConv.contesto === 'cliente' ? newConv.client_id || null : null,
        project_id: newConv.contesto === 'progetto' ? newConv.project_id || null : null,
        priorita: newConv.priorita || null, assegnato_a: newConv.assegnato_a || null,
      };
      const res = await chatAPI.createConversation(payload);
      await loadConversations();
      setNewConvOpen(false);
      setNewConv({ titolo: '', contesto: 'generale', client_id: '', project_id: '', priorita: '', assegnato_a: '' });
      handleSelectConv(res.data);
      toast.success('Conversazione creata');
    } catch { toast.error('Errore creazione'); }
  };

  const getUser = (id) => users.find(u => u.id === id);
  const getClientName = (id) => clients.find(c => c.id === id)?.nome ?? '';
  const getProjectName = (id) => projects.find(p => p.id === id)?.nome ?? '';
  const getConvSubtitle = (conv) => {
    if (conv.contesto === 'cliente' && conv.client_id) return getClientName(conv.client_id);
    if (conv.contesto === 'progetto' && conv.project_id) return getProjectName(conv.project_id);
    return 'Generale';
  };

  const filtered = conversations.filter(c => c.titolo.toLowerCase().includes(searchQuery.toLowerCase()));
  const generale = filtered.filter(c => c.contesto === 'generale');
  const perCliente = filtered.filter(c => c.contesto === 'cliente');
  const perProgetto = filtered.filter(c => c.contesto === 'progetto');
  const focusCount = focus ? (focus.domande_aperte?.length ?? 0) + (focus.urgenti?.length ?? 0) : 0;

  return (
    <Layout title="Chat Team">
      {/* Contenitore principale: su desktop side-by-side, su mobile schermata singola */}
      <div className="flex h-[calc(100vh-8rem)] rounded-xl border border-slate-200 overflow-hidden bg-white">

        {/* ── PANNELLO SINISTRO (lista conversazioni) ──
            Desktop: sempre visibile (flex)
            Mobile: visibile solo quando mobileView === 'list' */}
        <div className={cn(
          'flex flex-col border-r border-slate-200 bg-white',
          // mobile: full width quando visibile, nascosto quando siamo in chat
          'w-full lg:w-72 lg:flex-shrink-0',
          mobileView === 'chat' ? 'hidden lg:flex' : 'flex',
        )}>
          {/* Header */}
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800 text-base">Chat</h2>
            <Dialog open={newConvOpen} onOpenChange={setNewConvOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 h-9 px-3">
                  <Plus className="h-4 w-4 mr-1" /> Nuova
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle>Nuova Conversazione</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="space-y-1.5">
                    <Label>Titolo *</Label>
                    <Input placeholder="Es. Revisione logo" value={newConv.titolo}
                      onChange={e => setNewConv(p => ({ ...p, titolo: e.target.value }))} autoFocus />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Contesto</Label>
                    <Select value={newConv.contesto} onValueChange={v => setNewConv(p => ({ ...p, contesto: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="generale">Generale</SelectItem>
                        <SelectItem value="cliente">Cliente</SelectItem>
                        <SelectItem value="progetto">Progetto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newConv.contesto === 'cliente' && (
                    <div className="space-y-1.5">
                      <Label>Cliente</Label>
                      <Select value={newConv.client_id} onValueChange={v => setNewConv(p => ({ ...p, client_id: v }))}>
                        <SelectTrigger><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                        <SelectContent>
                          {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {newConv.contesto === 'progetto' && (
                    <div className="space-y-1.5">
                      <Label>Progetto</Label>
                      <Select value={newConv.project_id} onValueChange={v => setNewConv(p => ({ ...p, project_id: v }))}>
                        <SelectTrigger><SelectValue placeholder="Seleziona progetto" /></SelectTrigger>
                        <SelectContent>
                          {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Priorità</Label>
                      <Select value={newConv.priorita} onValueChange={v => setNewConv(p => ({ ...p, priorita: v }))}>
                        <SelectTrigger><SelectValue placeholder="Nessuna" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bassa">🟢 Bassa</SelectItem>
                          <SelectItem value="media">🟡 Media</SelectItem>
                          <SelectItem value="alta">🟠 Alta</SelectItem>
                          <SelectItem value="critica">🔴 Critica</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Assegnato a</Label>
                      <Select value={newConv.assegnato_a} onValueChange={v => setNewConv(p => ({ ...p, assegnato_a: v }))}>
                        <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                        <SelectContent>
                          {users.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setNewConvOpen(false)}>Annulla</Button>
                    <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleCreateConv}>Crea</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-100">
            <button
              onClick={() => setActiveTab('conversations')}
              className={cn('flex-1 py-2.5 text-xs font-medium transition-colors',
                activeTab === 'conversations' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500')}
            >
              Conversazioni
            </button>
            <button
              onClick={() => setActiveTab('focus')}
              className={cn('flex-1 py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-1',
                activeTab === 'focus' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500')}
            >
              Focus
              {focusCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center leading-none">
                  {focusCount > 9 ? '9+' : focusCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={cn('flex-1 py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-1',
                activeTab === 'search' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-slate-500')}
            >
              <Search className="h-3 w-3" />
              Cerca
            </button>
          </div>

          {activeTab === 'search' ? (
            <SearchPanel
              q={searchQ}
              onQChange={handleSearchChange}
              loading={searchLoading}
              results={searchResults}
              onSelectConv={(convId, msgId) => {
                const conv = conversations.find(c => c.id === convId);
                if (conv) handleSelectConv(conv, msgId || null);
              }}
            />
          ) : activeTab === 'conversations' ? (
            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="p-3 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input placeholder="Cerca..." value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" />
                </div>
              </div>
              <ScrollArea className="flex-1">
                <div className="py-2">
                  {generale.length > 0 && (
                    <ConvGroup title="Generale" convs={generale} selectedId={selectedConv?.id}
                      onSelect={handleSelectConv} getSubtitle={getConvSubtitle} />
                  )}
                  {perCliente.length > 0 && (
                    <ConvGroup title="Clienti" convs={perCliente} selectedId={selectedConv?.id}
                      onSelect={handleSelectConv} getSubtitle={getConvSubtitle} />
                  )}
                  {perProgetto.length > 0 && (
                    <ConvGroup title="Progetti" convs={perProgetto} selectedId={selectedConv?.id}
                      onSelect={handleSelectConv} getSubtitle={getConvSubtitle} />
                  )}
                  {filtered.length === 0 && (
                    <p className="text-center text-sm text-slate-400 py-8">Nessuna conversazione</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          ) : (
            <FocusPanel focus={focus} onSelectConv={(convId) => {
              const conv = conversations.find(c => c.id === convId);
              if (conv) { handleSelectConv(conv); setActiveTab('conversations'); }
            }} getUser={getUser} />
          )}
        </div>

        {/* ── PANNELLO DESTRO (messaggi) ──
            Desktop: sempre visibile
            Mobile: visibile solo quando mobileView === 'chat' */}
        <div className={cn(
          'flex-1 flex flex-col min-w-0 bg-white',
          mobileView === 'list' ? 'hidden lg:flex' : 'flex',
        )}>
          {!selectedConv ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-400">
              <MessagesSquare className="h-12 w-12 opacity-30" />
              <p className="text-sm">Seleziona una conversazione</p>
            </div>
          ) : (
            <>
              {/* Header conversazione */}
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                {/* Bottone back — visibile solo su mobile */}
                <button
                  onClick={handleBack}
                  className="lg:hidden p-1.5 -ml-1 rounded-lg text-slate-500 hover:bg-slate-100"
                  aria-label="Torna alla lista"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {selectedConv.priorita && PRIORITY_CONFIG[selectedConv.priorita] && (
                      <span className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', PRIORITY_CONFIG[selectedConv.priorita].dot)} />
                    )}
                    <h3 className="font-semibold text-slate-900 truncate text-sm">{selectedConv.titolo}</h3>
                    {selectedConv.ticket_number && (
                      <span className="text-xs font-mono text-slate-400 flex-shrink-0 hidden sm:inline">
                        {selectedConv.ticket_number}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {getConvSubtitle(selectedConv)}
                    {selectedConv.assegnato_a && (() => {
                      const u = getUser(selectedConv.assegnato_a);
                      return u ? ` · ${u.nome} ${u.cognome}` : '';
                    })()}
                  </p>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* Su mobile mostra solo il badge stato, su desktop anche il select */}
                  <div className="hidden sm:block">
                    <Select value={selectedConv.stato} onValueChange={v => handleStatusChange(selectedConv.id, v)}>
                      <SelectTrigger className="h-7 text-xs w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(s => (
                          <SelectItem key={s} value={s}>
                            <span className={cn('text-xs font-medium')}>{STATUS_CONFIG[s].label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Badge className={cn('text-[10px] px-1.5 py-0 sm:hidden', STATUS_CONFIG[selectedConv.stato]?.class)}>
                    {STATUS_CONFIG[selectedConv.stato]?.label}
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {/* Su mobile permetti anche cambio stato dal menu */}
                      <div className="sm:hidden px-2 py-1.5 border-b border-slate-100 mb-1">
                        <p className="text-xs text-slate-400 mb-1.5">Stato</p>
                        {STATUSES.map(s => (
                          <button key={s} onClick={() => handleStatusChange(selectedConv.id, s)}
                            className={cn('w-full text-left text-xs px-2 py-1.5 rounded-md mb-0.5 font-medium',
                              selectedConv.stato === s ? STATUS_CONFIG[s].class : 'text-slate-600 hover:bg-slate-50')}>
                            {STATUS_CONFIG[s].label}
                          </button>
                        ))}
                      </div>
                      <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(selectedConv.id)}>
                        <Trash2 className="h-4 w-4 mr-2" /> Elimina conversazione
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Messaggi */}
              <ScrollArea className="flex-1 px-3 sm:px-5 py-3">
                <div className="space-y-3">
                  {messages.length === 0 && (
                    <p className="text-center text-sm text-slate-400 py-8">
                      Nessun messaggio. Inizia la conversazione.
                    </p>
                  )}
                  {messages.map(msg => {
                    const typeConf = MSG_TYPES[msg.tipo] ?? MSG_TYPES.info;
                    const TypeIcon = typeConf.icon;
                    const isWA = msg.source === 'whatsapp';
                    const msgUser = isWA ? null : getUser(msg.user_id);
                    const isMe = (!isWA && msg.user_id === currentUser?.id) || (isWA && msg.wa_from_me);
                    const senderName = isWA
                      ? (msg.wa_from_me ? 'Tu (WA)' : (msg.wa_sender_name || 'WhatsApp'))
                      : (msgUser ? `${msgUser.nome} ${msgUser.cognome}` : 'Utente');
                    const isHighlighted = msg.id === highlightMsgId;
                    return (
                      <div
                        key={msg.id}
                        id={`msg-${msg.id}`}
                        className={cn(
                          'flex gap-2 group rounded-xl transition-colors duration-1000',
                          isMe ? 'flex-row-reverse' : 'flex-row',
                          isHighlighted && 'bg-yellow-50',
                        )}
                      >
                        {/* Avatar */}
                        <div className={cn(
                          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold',
                          isWA ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700',
                        )}>
                          {isWA ? '📱' : userInitials(msgUser)}
                        </div>
                        {/* Bubble */}
                        <div className={cn('max-w-[80%] sm:max-w-[70%] space-y-1', isMe ? 'items-end' : 'items-start', 'flex flex-col')}>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            {!isMe && (
                              <span className={cn('font-medium', isWA ? 'text-emerald-600' : 'text-slate-600')}>
                                {senderName}
                                {isWA && <span className="ml-1 text-[10px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-full">WA</span>}
                              </span>
                            )}
                            <span>{formatMsgTime(msg.created_at)}</span>
                            {/* Edit button — visible on hover */}
                            {!msg.audio_data && (
                              <button
                                onClick={() => openEditMsg(msg)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"
                                title="Classifica messaggio"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          <div className={cn(
                            'rounded-2xl px-3 py-2 border text-sm',
                            typeConf.bg, typeConf.border,
                            msg.tipo === 'info' && isMe ? 'bg-indigo-600 border-indigo-600' : '',
                          )}>
                            {msg.tipo !== 'info' && (
                              <div className={cn('flex items-center gap-1 mb-1 text-xs font-semibold', typeConf.text)}>
                                <TypeIcon className="h-3 w-3" />
                                {typeConf.label}
                              </div>
                            )}
                            {msg.destinatari?.length > 0 && (
                              <p className="text-xs text-slate-400 mb-1">
                                → {msg.destinatari.map(id => {
                                  const u = getUser(id); return u ? u.nome : id;
                                }).join(', ')}
                              </p>
                            )}
                            {msg.audio_data ? (
                              <audio
                                controls
                                className="max-w-[240px] h-9 mt-1"
                                src={`data:${msg.audio_mime || 'audio/webm'};base64,${msg.audio_data}`}
                              />
                            ) : (
                              <p className={cn(
                                'whitespace-pre-wrap leading-relaxed text-sm',
                                msg.tipo === 'info' && isMe ? 'text-white' : 'text-slate-800',
                              )}>
                                {msg.testo}
                              </p>
                            )}
                            {/* Emoji reactions (da WhatsApp) */}
                            {msg.reactions && msg.reactions.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {Object.entries(
                                  msg.reactions.reduce((acc, r) => {
                                    acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                                    return acc;
                                  }, {})
                                ).map(([emoji, count]) => (
                                  <span
                                    key={emoji}
                                    className="inline-flex items-center gap-0.5 bg-white border border-slate-200 rounded-full px-1.5 py-0.5 text-xs shadow-sm"
                                    title={msg.reactions.filter(r => r.emoji === emoji).map(r => r.sender_name || '').filter(Boolean).join(', ')}
                                  >
                                    {emoji}{count > 1 && <span className="text-slate-500 text-[10px]">{count}</span>}
                                  </span>
                                ))}
                              </div>
                            )}
                            {msg.tipo === 'domanda' && (
                              <div className="mt-2">
                                {msg.risolto ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                                    <CheckCheck className="h-3 w-3" />
                                    Risolta {getUser(msg.risolto_da) ? `da ${getUser(msg.risolto_da).nome}` : ''}
                                  </span>
                                ) : (
                                  <Button size="sm" variant="outline"
                                    className="h-6 text-xs px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                                    onClick={() => handleResolve(msg.id)}>
                                    Segna come risolta
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Area input */}
              <div className="px-3 sm:px-5 py-3 border-t border-slate-100 space-y-2">
                {/* Destinatari */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs text-slate-400 font-medium">A:</span>
                  <button
                    onClick={() => setDestinatari([])}
                    className={cn('text-xs px-2.5 py-1 rounded-full border transition-all',
                      destinatari.length === 0
                        ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                        : 'bg-white text-slate-400 border-slate-200')}
                  >
                    Tutti
                  </button>
                  {users.filter(u => u.id !== currentUser?.id).map(u => (
                    <button key={u.id}
                      onClick={() => setDestinatari(prev =>
                        prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id]
                      )}
                      className={cn('text-xs px-2.5 py-1 rounded-full border transition-all',
                        destinatari.includes(u.id)
                          ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                          : 'bg-white text-slate-400 border-slate-200')}
                    >
                      {u.nome}
                    </button>
                  ))}
                </div>

                {/* Tipo messaggio — scrollabile orizzontalmente su mobile */}
                <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                  {Object.entries(MSG_TYPES).map(([key, conf]) => {
                    const Icon = conf.icon;
                    return (
                      <button key={key} onClick={() => setNewMsgType(key)}
                        className={cn(
                          'flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-all flex-shrink-0',
                          newMsgType === key
                            ? cn(conf.bg, conf.text, conf.border, 'shadow-sm')
                            : 'bg-white text-slate-400 border-slate-200',
                        )}
                      >
                        <Icon className="h-3 w-3" />
                        <span className="hidden sm:inline">{conf.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Anteprima audio registrato */}
                {pendingAudio && (
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-3 py-2">
                    <Mic className="h-4 w-4 text-indigo-500 shrink-0" />
                    <audio
                      controls
                      className="h-9 flex-1 min-w-0"
                      src={`data:${pendingAudio.mime};base64,${pendingAudio.data}`}
                    />
                    <button onClick={cancelAudio} className="text-slate-400 hover:text-red-500 transition-colors shrink-0">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Testo + microfono + invio */}
                <div className="flex gap-2 items-end">
                  {!pendingAudio && (
                    <Textarea
                      ref={textareaRef}
                      placeholder="Scrivi un messaggio…"
                      value={newMsgText}
                      onChange={e => setNewMsgText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={2}
                      className="resize-none text-sm flex-1 text-base"
                    />
                  )}
                  {pendingAudio && <div className="flex-1" />}

                  {/* Mic button */}
                  {!pendingAudio && (
                    <Button
                      type="button"
                      variant={isRecording ? 'destructive' : 'outline'}
                      className={cn(
                        'h-[60px] w-12 px-0 shrink-0 transition-all',
                        isRecording && 'animate-pulse',
                      )}
                      onClick={isRecording ? stopRecording : startRecording}
                      disabled={sending}
                      title={isRecording ? `Ferma (${recordingSeconds}s)` : 'Registra messaggio audio'}
                    >
                      {isRecording
                        ? <><Square className="h-4 w-4" /><span className="text-[10px] mt-0.5 block leading-none">{recordingSeconds}s</span></>
                        : <Mic className="h-4 w-4" />}
                    </Button>
                  )}

                  {/* Send button */}
                  <Button
                    className="bg-indigo-600 hover:bg-indigo-700 h-[60px] w-12 px-0 shrink-0"
                    onClick={pendingAudio ? sendAudio : handleSend}
                    disabled={sending || (pendingAudio ? false : !newMsgText.trim())}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Edit / Classify Message Dialog ── */}
      <Dialog open={!!editingMsg} onOpenChange={(open) => { if (!open) setEditingMsg(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-indigo-500" />
              Classifica messaggio
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            {/* Testo */}
            <div className="space-y-1.5">
              <Label>Testo</Label>
              <Textarea
                value={editTesto}
                onChange={e => setEditTesto(e.target.value)}
                rows={3}
                className="resize-none text-sm"
              />
            </div>

            {/* Tipo */}
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(MSG_TYPES).map(([key, conf]) => {
                  const Icon = conf.icon;
                  return (
                    <button key={key} onClick={() => setEditTipo(key)}
                      className={cn(
                        'flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-all',
                        editTipo === key
                          ? cn(conf.bg, conf.text, conf.border, 'shadow-sm')
                          : 'bg-white text-slate-400 border-slate-200',
                      )}
                    >
                      <Icon className="h-3 w-3" />
                      {conf.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Assegna a */}
            <div className="space-y-1.5">
              <Label>Assegna a</Label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setEditDestinatari([])}
                  className={cn('text-xs px-2.5 py-1 rounded-full border transition-all',
                    editDestinatari.length === 0
                      ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                      : 'bg-white text-slate-400 border-slate-200')}
                >
                  Tutti
                </button>
                {users.map(u => (
                  <button key={u.id}
                    onClick={() => setEditDestinatari(prev =>
                      prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id]
                    )}
                    className={cn('text-xs px-2.5 py-1 rounded-full border transition-all',
                      editDestinatari.includes(u.id)
                        ? 'bg-indigo-100 text-indigo-700 border-indigo-300'
                        : 'bg-white text-slate-400 border-slate-200')}
                  >
                    {u.nome} {u.cognome}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setEditingMsg(null)}>Annulla</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUpdateMsg}>
                Salva
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// ── SearchPanel ───────────────────────────────────────────────
const CONTESTO_LABEL = { generale: 'Generale', cliente: 'Cliente', progetto: 'Progetto', whatsapp: 'WhatsApp' };

const highlightMatch = (text, q) => {
  if (!q || !text) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text.slice(0, 120);
  const start = Math.max(0, idx - 40);
  const end = Math.min(text.length, idx + q.length + 80);
  const snippet = (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
  // Return with bold highlight
  const rel = idx - start + (start > 0 ? 1 : 0); // offset inside snippet accounting for '…'
  return snippet;
};

const SearchPanel = ({ q, onQChange, loading, results, onSelectConv }) => (
  <div className="flex flex-col flex-1 overflow-hidden">
    <div className="p-3 border-b border-slate-100">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          autoFocus
          value={q}
          onChange={e => onQChange(e.target.value)}
          placeholder="Cerca nelle chat…"
          className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
        />
        {q && (
          <button onClick={() => onQChange('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>

    <ScrollArea className="flex-1">
      {/* Idle */}
      {!q && (
        <p className="text-center text-xs text-slate-400 py-10 px-4">
          Digita almeno 2 caratteri per cercare in tutti i messaggi
        </p>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-10 gap-2 text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">Ricerca in corso…</span>
        </div>
      )}

      {/* No results */}
      {!loading && results && results.total === 0 && (
        <p className="text-center text-xs text-slate-400 py-10">Nessun risultato per "{q}"</p>
      )}

      {/* Results */}
      {!loading && results && results.total > 0 && (
        <div className="py-2">
          <p className="px-4 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
            {results.total} risultat{results.total === 1 ? 'o' : 'i'}
          </p>
          {results.results.map(group => (
            <div key={group.conversation_id} className="mb-1">
              {/* Conversation header */}
              <button
                onClick={() => onSelectConv(group.conversation_id)}
                className="w-full text-left px-4 py-2 hover:bg-indigo-50 active:bg-indigo-100 transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium">
                    {CONTESTO_LABEL[group.conversation_contesto] || group.conversation_contesto}
                  </span>
                  <span className="text-sm font-semibold text-indigo-700 truncate">{group.conversation_titolo}</span>
                  {group.total_hits > group.messages.length && (
                    <span className="ml-auto text-[10px] text-slate-400 flex-shrink-0">
                      +{group.total_hits - group.messages.length} altri
                    </span>
                  )}
                </div>
              </button>

              {/* Message snippets */}
              {group.messages.map(msg => {
                const typeConf = MSG_TYPES[msg.tipo] ?? MSG_TYPES.info;
                const TypeIcon = typeConf.icon;
                const snippet = highlightMatch(msg.testo, q);
                const senderName = msg.source === 'whatsapp'
                  ? (msg.wa_from_me ? 'Tu (WA)' : (msg.wa_sender_name || 'WhatsApp'))
                  : null;
                return (
                  <button
                    key={msg.id}
                    onClick={() => onSelectConv(msg.conversation_id, msg.id)}
                    className="w-full text-left px-4 py-2 border-l-2 border-transparent hover:border-indigo-300 hover:bg-slate-50 transition-colors ml-0 block"
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {msg.tipo !== 'info' && (
                        <span className={cn('flex items-center gap-0.5 text-[10px] font-semibold', typeConf.text)}>
                          <TypeIcon className="h-2.5 w-2.5" />
                          {typeConf.label}
                        </span>
                      )}
                      {senderName && (
                        <span className="text-[10px] text-emerald-600 font-medium">{senderName}</span>
                      )}
                      <span className="ml-auto text-[10px] text-slate-400 flex-shrink-0">
                        {msg.created_at ? format(new Date(msg.created_at), 'dd/MM/yy HH:mm', { locale: it }) : ''}
                      </span>
                    </div>
                    <SnippetText text={snippet} q={q} />
                  </button>
                );
              })}
              <Separator className="my-1" />
            </div>
          ))}
        </div>
      )}
    </ScrollArea>
  </div>
);

// Renders text with the search term highlighted in bold
const SnippetText = ({ text, q }) => {
  if (!q || !text) return <p className="text-xs text-slate-600 truncate">{text}</p>;
  const lower = text.toLowerCase();
  const lowerQ = q.toLowerCase();
  const idx = lower.indexOf(lowerQ);
  if (idx === -1) return <p className="text-xs text-slate-600 line-clamp-2">{text}</p>;
  return (
    <p className="text-xs text-slate-600 line-clamp-2">
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 text-slate-800 rounded px-0.5 font-semibold not-italic">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </p>
  );
};

// ── ConvGroup ─────────────────────────────────────────────────
const ConvGroup = ({ title, convs, selectedId, onSelect, getSubtitle }) => (
  <div className="mb-1">
    <p className="px-4 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{title}</p>
    {convs.map(conv => (
      <button key={conv.id} onClick={() => onSelect(conv)}
        className={cn(
          'w-full text-left px-4 py-3 transition-colors hover:bg-slate-50 active:bg-slate-100',
          selectedId === conv.id ? 'bg-indigo-50 border-r-2 border-indigo-500' : '',
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              {conv.priorita && PRIORITY_CONFIG[conv.priorita] && (
                <span className={cn('w-2 h-2 rounded-full flex-shrink-0', PRIORITY_CONFIG[conv.priorita].dot)} />
              )}
              <p className={cn('text-sm font-medium truncate', selectedId === conv.id ? 'text-indigo-700' : 'text-slate-800')}>
                {conv.ticket_number
                  ? <span className="text-slate-400 font-mono text-xs mr-1">{conv.ticket_number}</span>
                  : null}
                {conv.titolo}
              </p>
            </div>
            <p className="text-xs text-slate-400 truncate mt-0.5">
              {conv.last_message_preview || getSubtitle(conv)}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
            {conv.has_unread && (
              <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" title="Messaggi non letti" />
            )}
            <Badge className={cn('text-[10px] px-1.5 py-0', STATUS_CONFIG[conv.stato]?.class)}>
              {STATUS_CONFIG[conv.stato]?.label}
            </Badge>
            <ChevronRight className="h-4 w-4 text-slate-300 lg:hidden" />
          </div>
        </div>
      </button>
    ))}
  </div>
);

// ── FocusPanel ────────────────────────────────────────────────
const FocusPanel = ({ focus, onSelectConv, getUser }) => {
  if (!focus) return <div className="flex-1 flex items-center justify-center"><div className="spinner" /></div>;

  const Section = ({ icon: Icon, title, color, items, renderItem }) => (
    <div className="mb-1">
      <div className={cn('flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider', color)}>
        <Icon className="h-3.5 w-3.5" />
        {title}
        <span className="ml-auto bg-white/60 rounded-full px-1.5 py-0.5">{items.length}</span>
      </div>
      {items.length === 0
        ? <p className="px-4 py-2 text-xs text-slate-400 italic">Nessun elemento</p>
        : items.map(renderItem)}
    </div>
  );

  const MsgItem = (item, key) => (
    <button key={key} onClick={() => onSelectConv(item.conversation_id)}
      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 active:bg-slate-100 border-b border-slate-50 last:border-0">
      <p className="text-sm font-medium text-slate-700 truncate">{item.conversation_titolo}</p>
      <p className="text-xs text-slate-500 truncate mt-0.5">{item.testo}</p>
    </button>
  );

  return (
    <ScrollArea className="flex-1">
      <div className="py-2">
        <Section icon={AlertCircle} title="Urgenti" color="text-red-600 bg-red-50"
          items={focus.urgenti ?? []} renderItem={(i) => MsgItem(i, i.id)} />
        <Separator className="my-1" />
        <Section icon={HelpCircle} title="Domande aperte" color="text-amber-600 bg-amber-50"
          items={focus.domande_aperte ?? []} renderItem={(i) => MsgItem(i, i.id)} />
        <Separator className="my-1" />
        <Section icon={ClipboardList} title="Task" color="text-violet-600 bg-violet-50"
          items={focus.tasks ?? []} renderItem={(i) => MsgItem(i, i.id)} />
        <Separator className="my-1" />
        <Section icon={CheckCircle2} title="Decisioni recenti" color="text-emerald-600 bg-emerald-50"
          items={focus.decisioni_recenti ?? []} renderItem={(i) => MsgItem(i, i.id)} />
      </div>
    </ScrollArea>
  );
};
